import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { DetailPayload } from "./apiTypes";
import { orderedAnswerEntries } from "./formatAnswers";

export default function AdminDetail() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<DetailPayload | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setErr(null);
    try {
      const res = await fetch(`/api/admin/response/${id}`, { credentials: "include" });
      if (!res.ok) {
        setErr("No se pudo cargar.");
        return;
      }
      setDetail((await res.json()) as DetailPayload);
    } catch {
      setErr("Error de red.");
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function copySummary() {
    if (!detail?.copySummary) return;
    try {
      await navigator.clipboard.writeText(detail.copySummary);
    } catch {
      /* ignore */
    }
  }

  if (!id) {
    return <p className="text-sm text-pp-muted">ID inválido.</p>;
  }

  if (err || !detail) {
    return (
      <div className="text-sm space-y-2">
        <p className="text-red-700">{err ?? "Cargando…"}</p>
        <Link to="/admin/responses" className="underline">
          Volver a respuestas
        </Link>
      </div>
    );
  }

  const row = detail.response;
  const profile = (row.profile as string) ?? null;
  let answers: Record<string, unknown> = {};
  try {
    answers = JSON.parse((row.answers_json as string) || "{}") as Record<string, unknown>;
  } catch {
    answers = {};
  }

  const entries = orderedAnswerEntries(profile, answers);

  return (
    <div className="space-y-4 text-sm">
      <div className="flex flex-wrap gap-3 items-center">
        <Link to="/admin/responses" className="underline">
          ← Respuestas
        </Link>
        <button
          type="button"
          className="border rounded-md px-3 py-1.5 text-sm"
          onClick={() => void copySummary()}
        >
          Copiar resumen
        </button>
      </div>

      <p className="text-xs text-pp-quiet">
        {String(row.id)} · {String(row.created_at)}
      </p>

      <div className="border rounded p-3 bg-pp-soft space-y-1">
        <p>
          <span className="font-medium">Score total</span> {detail.breakdown.score_total} · segmento{" "}
          {String(row.segment)} · señal {String(row.signal_level)}
        </p>
        <p className="text-pp-muted">
          Pain {detail.breakdown.score_pain} · willingness {detail.breakdown.score_willingness} ·
          penalty {detail.breakdown.score_penalty}
        </p>
      </div>

      {detail.contact && (
        <p>
          <span className="font-medium">Email</span> {detail.contact.email}
        </p>
      )}

      {detail.riskFlags.length > 0 && (
        <div className="border-l-4 border-amber-600 pl-3 py-1 bg-amber-50/50">
          <p className="font-medium text-amber-900">Banderas</p>
          <ul className="list-disc pl-5 mt-1">
            {detail.riskFlags.map((x, i) => (
              <li key={i}>{x}</li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <p className="font-medium">Siguiente paso</p>
        <p className="text-pp-main">{detail.nextAction}</p>
      </div>

      <div>
        <p className="font-medium mb-2">Respuestas</p>
        <ul className="space-y-2 border rounded-md divide-y">
          {entries.map((e) => (
            <li key={e.id} className="p-3">
              <p className="text-xs text-pp-quiet">{e.label}</p>
              <p className="text-pp-main whitespace-pre-wrap">{e.text}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
